"use client";

import { useTranslations } from "next-intl";
import { AlertTriangle } from "lucide-react";
import { DialogDescription } from "@/components/ui/dialog";
import { DialogCard } from "@/components/ui/dialog-card";
import { Button } from "@/components/ui/button";
import { createPortal } from "react-dom";
import { useMediaQuery } from "@/hooks/use-media-query";
import type { ReserveErrorCode } from "@/lib/cart/cart-reserve";

export type { ReserveErrorCode };

interface Props {
  open: boolean;
  onCloseAction: () => void;
  listingTitles: string[];
  errorCode?: ReserveErrorCode;
}

export function OutOfStockModal({ open, onCloseAction, listingTitles, errorCode }: Props) {
  const t = useTranslations("outOfStock");
  const first = listingTitles[0] || "Item";
  const extraCount = Math.max(0, listingTitles.length - 1);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const getContent = () => {
    switch (errorCode) {
      case "PROFILE_NOT_FOUND":
        return {
          title: t("accountIncomplete"),
          description: t("accountIncompleteDesc"),
          showRetry: false,
        };
      case "LISTING_DELETED":
        return {
          title: extraCount > 0 ? t("noLongerAvailableMultiple") : t("noLongerAvailableSingle", { title: first }),
          description: t("removedBySeller"),
          showRetry: false,
        };
      case "SELLER_PAYMENT_ERROR":
        return {
          title: t("sellerPaymentError"),
          description: extraCount > 0 ? t("sellerPaymentErrorMultiple") : t("sellerPaymentErrorSingle", { title: first }),
          showRetry: false,
        };
      case "ITEMS_UNAVAILABLE":
      default:
        return {
          title: extraCount > 0 ? t("someSorryMultiple") : t("someSorrySingle", { title: first }),
          description: null,
          showRetry: true,
        };
    }
  };

  const content = getContent();

  const modalContent = (
    <DialogCard
      open={open}
      onOpenChange={(v) => { if (!v) onCloseAction(); }}
      chrome="titleOnly"
      title={content.title}
      panelClassName="max-w-sm"
    >
      <div className="flex justify-center pb-3">
        <div className="p-2 rounded-full bg-amber-500/20"><AlertTriangle className="h-5 w-5 text-amber-500" /></div>
      </div>
      <DialogDescription className="text-muted-foreground">
        {content.description ? (
          <div>{content.description}</div>
        ) : extraCount > 0 ? (
          <div className="space-y-2">
            <div>{t("beingPurchased")}</div>
            <ul className="list-disc pl-5 space-y-1">
              {listingTitles.slice(0, 5).map((t, idx) => (
                <li key={idx}><span className="font-medium">{t}</span></li>
              ))}
            </ul>
            {listingTitles.length > 5 && (
              <div className="text-muted-foreground">{t("andMore", { count: listingTitles.length - 5 })}</div>
            )}
          </div>
        ) : (
          <div>{t("currentlyPurchased")}</div>
        )}
        {content.showRetry && <div className="mt-1">{t("tryAgain")}</div>}
      </DialogDescription>
      <div className="mt-4 flex justify-end">
        <Button onClick={onCloseAction} className="rounded-full">{t("ok")}</Button>
      </div>
    </DialogCard>
  );

  // On mobile, render modal outside BottomSheet using portal to show overlay above cart
  if (isMobile && typeof window !== 'undefined') {
    return createPortal(modalContent, document.body);
  }
  
  // On desktop, render normally
  return modalContent;
}


